// Graph algorithms — emit the shared event stream (init-graph / set-node-state /
// set-edge-state / node-pointer / vars). Node positions are authored by hand.

// ─── Undirected sample graph ─────────────────────────────────────────────────
const UNDIRECTED = {
  nodes: [
    { id: 0, value: 'A', x: -4.5, y: 2.0 },
    { id: 1, value: 'B', x: -1.7, y: 3.0 },
    { id: 2, value: 'C', x: -1.7, y: 0.0 },
    { id: 3, value: 'D', x: 1.5,  y: 2.2 },
    { id: 4, value: 'E', x: 1.5,  y: -1.4 },
    { id: 5, value: 'F', x: 4.4,  y: 0.6 },
  ],
  edges: [
    { from: 0, to: 1 }, { from: 0, to: 2 }, { from: 1, to: 3 },
    { from: 2, to: 3 }, { from: 2, to: 4 }, { from: 3, to: 5 }, { from: 4, to: 5 },
  ],
}

const adjacency = (nodes, edges, directed = false) => {
  const adj = {}
  nodes.forEach((n) => { adj[n.id] = [] })
  edges.forEach(({ from, to }) => {
    adj[from].push(to)
    if (!directed) adj[to].push(from)
  })
  Object.values(adj).forEach((l) => l.sort((a, b) => a - b))
  return adj
}

const cloneGraph = (g) => ({
  nodes: g.nodes.map((n) => ({ ...n })),
  edges: g.edges.map((e) => ({ ...e })),
})

// ─── Graph BFS ───────────────────────────────────────────────────────────────
export function graphBFS() {
  const g = cloneGraph(UNDIRECTED)
  const adj = adjacency(g.nodes, g.edges)
  const label = (id) => g.nodes.find((n) => n.id === id).value
  const steps = []
  steps.push({
    type: 'init-graph', nodes: g.nodes, edges: g.edges,
    operation: 'Initialize',
    explanation: 'Breadth-First Search from A: explore neighbours level by level using a queue.',
  })

  const visited = new Set([0])
  const queue = [0]
  const order = []
  steps.push({ type: 'set-node-state', ids: [0], state: 'compared',
    explanation: 'Enqueue start node A and mark it discovered' })
  steps.push({ type: 'vars', vars: { queue: '[A]', visited: '{A}' } })

  while (queue.length) {
    const id = queue.shift()
    steps.push({ type: 'node-pointer', name: 'cur', id })
    steps.push({ type: 'set-node-state', ids: [id], state: 'active',
      operation: `Dequeue ${label(id)}`, explanation: `Visit ${label(id)} and scan its neighbours` })
    order.push(label(id))

    for (const nb of adj[id]) {
      if (!visited.has(nb)) {
        visited.add(nb)
        queue.push(nb)
        steps.push({ type: 'set-edge-state', edges: [[id, nb]], state: 'active' })
        steps.push({ type: 'set-node-state', ids: [nb], state: 'compared',
          explanation: `Discover ${label(nb)} → enqueue it` })
      }
    }
    steps.push({ type: 'vars', vars: {
      queue: `[${queue.map(label).join(', ')}]`,
      visited: `{${[...visited].map(label).join(', ')}}`,
      order: `[${order.join(', ')}]`,
    } })
    steps.push({ type: 'set-node-state', ids: [id], state: 'visited' })
  }
  steps.push({ type: 'node-pointer', name: 'cur', remove: true })
  steps.push({ type: 'set-node-state', ids: g.nodes.map((n) => n.id), state: 'answer',
    operation: 'BFS complete', explanation: `Visited order: ${order.join(' → ')}` })
  return steps
}

// ─── Graph DFS ───────────────────────────────────────────────────────────────
export function graphDFS() {
  const g = cloneGraph(UNDIRECTED)
  const adj = adjacency(g.nodes, g.edges)
  const label = (id) => g.nodes.find((n) => n.id === id).value
  const steps = []
  steps.push({
    type: 'init-graph', nodes: g.nodes, edges: g.edges,
    operation: 'Initialize',
    explanation: 'Depth-First Search from A: follow each path as deep as possible before backtracking.',
  })

  const visited = new Set()
  const order = []
  function dfs(id, parent) {
    visited.add(id)
    if (parent != null) steps.push({ type: 'set-edge-state', edges: [[parent, id]], state: 'active' })
    steps.push({ type: 'node-pointer', name: 'cur', id })
    steps.push({ type: 'set-node-state', ids: [id], state: 'active',
      operation: `Visit ${label(id)}`, explanation: `Go as deep as possible from ${label(id)}` })
    order.push(label(id))
    steps.push({ type: 'vars', vars: { order: `[${order.join(', ')}]`,
      visited: `{${[...visited].map(label).join(', ')}}` } })
    for (const nb of adj[id]) {
      if (!visited.has(nb)) dfs(nb, id)
    }
    steps.push({ type: 'set-node-state', ids: [id], state: 'visited',
      explanation: `Backtrack from ${label(id)}` })
  }
  dfs(0, null)
  steps.push({ type: 'node-pointer', name: 'cur', remove: true })
  steps.push({ type: 'set-node-state', ids: g.nodes.map((n) => n.id), state: 'answer',
    operation: 'DFS complete', explanation: `Visited order: ${order.join(' → ')}` })
  return steps
}

// ─── Course Schedule (topological sort, Kahn's algorithm) ────────────────────
const DIRECTED = {
  nodes: [
    { id: 0, value: 'C0', x: -4.5, y: 1.5 },
    { id: 1, value: 'C1', x: -1.8, y: 2.8 },
    { id: 2, value: 'C2', x: -1.8, y: 0.0 },
    { id: 3, value: 'C3', x: 1.2,  y: 1.5 },
    { id: 4, value: 'C4', x: 4.2,  y: 1.5 },
    { id: 5, value: 'C5', x: 1.2,  y: -1.6 },
  ],
  // prerequisite (from) → course (to)
  edges: [
    { from: 0, to: 1, directed: true }, { from: 0, to: 2, directed: true },
    { from: 1, to: 3, directed: true }, { from: 2, to: 3, directed: true },
    { from: 3, to: 4, directed: true }, { from: 2, to: 5, directed: true },
  ],
}

export function courseSchedule() {
  const g = cloneGraph(DIRECTED)
  const adj = adjacency(g.nodes, g.edges, true)
  const label = (id) => g.nodes.find((n) => n.id === id).value
  const steps = []
  steps.push({
    type: 'init-graph', nodes: g.nodes, edges: g.edges,
    operation: 'Initialize',
    explanation: "Course Schedule — can all courses be finished? Use Kahn's topological sort: repeatedly take a course with no remaining prerequisites.",
  })

  const indeg = {}
  g.nodes.forEach((n) => { indeg[n.id] = 0 })
  g.edges.forEach((e) => { indeg[e.to]++ })
  steps.push({ type: 'vars', vars: Object.fromEntries(g.nodes.map((n) => [`indeg ${n.value}`, indeg[n.id]])),
    explanation: 'Compute in-degree (number of prerequisites) for every course.' })

  const queue = g.nodes.filter((n) => indeg[n.id] === 0).map((n) => n.id)
  queue.forEach((id) => {
    steps.push({ type: 'set-node-state', ids: [id], state: 'compared',
      explanation: `${label(id)} has no prerequisites — ready to take` })
  })

  const order = []
  while (queue.length) {
    const id = queue.shift()
    steps.push({ type: 'node-pointer', name: 'take', id })
    steps.push({ type: 'set-node-state', ids: [id], state: 'active',
      operation: `Take ${label(id)}`, explanation: `Complete ${label(id)} and unlock its dependents` })
    order.push(label(id))
    for (const nb of adj[id]) {
      steps.push({ type: 'set-edge-state', edges: [[id, nb]], state: 'active' })
      indeg[nb]--
      steps.push({ type: 'vars', vars: { [`indeg ${label(nb)}`]: indeg[nb] } })
      if (indeg[nb] === 0) {
        queue.push(nb)
        steps.push({ type: 'set-node-state', ids: [nb], state: 'compared',
          explanation: `${label(nb)} now has all prerequisites met` })
      }
    }
    steps.push({ type: 'set-node-state', ids: [id], state: 'visited' })
    steps.push({ type: 'vars', vars: { taken: `[${order.join(', ')}]` } })
  }

  steps.push({ type: 'node-pointer', name: 'take', remove: true })
  if (order.length === g.nodes.length) {
    steps.push({ type: 'set-node-state', ids: g.nodes.map((n) => n.id), state: 'answer',
      operation: 'Possible ✓', explanation: `All courses completable. Valid order: ${order.join(' → ')}` })
  } else {
    steps.push({ type: 'set-node-state', ids: g.nodes.filter((n) => indeg[n.id] > 0).map((n) => n.id), state: 'rejected',
      operation: 'Impossible ✗', explanation: 'A cycle remains — these courses can never be taken.' })
  }
  return steps
}

// ─── Clone Graph (#133) ──────────────────────────────────────────────────────
export function cloneGraphProblem() {
  // Represent as adjacency list: 4 nodes
  const g = {
    nodes: [
      { id: 1, value: 1, x: -2.5, y:  1.5 },
      { id: 2, value: 2, x:  2.5, y:  1.5 },
      { id: 3, value: 3, x:  2.5, y: -1.5 },
      { id: 4, value: 4, x: -2.5, y: -1.5 },
    ],
    edges: [
      { from: 1, to: 2 }, { from: 2, to: 3 },
      { from: 3, to: 4 }, { from: 4, to: 1 },
    ],
  }
  const adj = { 1:[2,4], 2:[1,3], 3:[2,4], 4:[3,1] }
  const steps = []
  steps.push({ type: 'init-graph', nodes: g.nodes, edges: g.edges, operation: 'Initialize', explanation: 'Clone Graph: BFS from node 1. For each unvisited neighbor, create a clone and enqueue.' })

  const cloned = {}
  const queue = [1]
  cloned[1] = true
  steps.push({ type: 'set-node-state', ids: [1], state: 'active', operation: 'Start BFS', explanation: 'Clone node 1 and enqueue it.' })
  steps.push({ type: 'vars', vars: { cloned: '[1]', queue: '[1]' } })

  const clonedList = [1]
  while (queue.length) {
    const id = queue.shift()
    steps.push({ type: 'node-pointer', name: 'cur', id })
    steps.push({ type: 'set-node-state', ids: [id], state: 'visited', operation: `Process ${id}`, explanation: `Connecting clone of ${id} to its neighbors` })
    for (const nb of adj[id]) {
      steps.push({ type: 'set-edge-state', edges: [[id, nb]], state: 'active', explanation: `Edge ${id}→${nb}` })
      if (!cloned[nb]) {
        cloned[nb] = true
        clonedList.push(nb)
        queue.push(nb)
        steps.push({ type: 'set-node-state', ids: [nb], state: 'compared', explanation: `Node ${nb} not yet cloned — create clone and enqueue` })
        steps.push({ type: 'vars', vars: { cloned: `[${clonedList.join(',')}]`, queue: `[${queue.join(',')}]` } })
      } else {
        steps.push({ type: 'set-node-state', ids: [nb], state: 'visited', explanation: `Node ${nb} already cloned — just add edge reference` })
      }
    }
  }
  steps.push({ type: 'node-pointer', name: 'cur', remove: true })
  steps.push({ type: 'set-node-state', ids: g.nodes.map(n => n.id), state: 'answer', operation: 'Cloned ✓', explanation: 'All nodes cloned with correct adjacency lists.' })
  return steps
}
